@use 'style/utils';

$_CLASS: '.joy-radio';

:host {
    /**
     * @prop --radio-size: override the radio width/height
     * @prop --radio-color: the radio label color
     * @prop --radio-circle-color: default radio button color
     * @prop --radio-circle-color-hover: hover radio button color
     * @prop --radio-circle-color-focus: focus radio button color
     * @prop --radio-circle-color-disabled: disabled radio button color
     * @prop --radio-color-disabled: disabled radio label color
     * @prop --radio-circle-color-invalid: invalid radio button color
     * @prop --radio-circle-color-invalid-hover: invalid radio button color on hover
     * @prop --radio-border-color-focus: keyboard focus outline color
     */
    --radio-size: 20px;
    --radio-color: var(--joy-color-text-body);

    --radio-circle-color: var(--joy-color-secondary-30);
    --radio-circle-color-hover: var(--joy-color-state-hover);
    --radio-circle-color-focus: var(--joy-color-state-focus);

    --radio-circle-color-disabled: var(--joy-color-neutral-30);
    --radio-color-disabled: var(--joy-color-neutral-40);

    --radio-circle-color-invalid: var(--joy-color-error-50);
    --radio-circle-color-invalid-hover: var(--joy-color-error-90);

    --radio-border-color-focus: var(--joy-color-neutral-60);

    display: block;
    box-sizing: border-box;
    position: relative;
    color: var(--radio-color);
    margin-bottom: var(--joy-core-spacing-4);
    outline: none;
    @include utils.unselectable();
    @include utils.getFontProperties(primary-400);
}

@mixin radioBeforeDefault {
    position: relative;

    &::before {
        content: '';
        box-sizing: border-box;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        top: 0;
        flex-shrink: 0;
        width: var(--radio-size);
        height: var(--radio-size);
        margin-right: var(--joy-core-spacing-1);
        border: var(--joy-form-border-width) solid var(--radio-circle-color);
        background-color: var(--radio-circle-color);
        border-radius: var(--joy-core-radius-6);
        box-shadow: inset 0 0 0 12px white;
    }
}

:host([checked]) {
    z-index: 2;
}

:host(:focus-visible) input[type="radio"] {
    ~ * {
        &:after {
            box-sizing: border-box;
            box-shadow: 0 0 0 1px var(--radio-border-color-focus);
        }
    }
}

:host([theme="outline"]) {
    width: 100%;
}

#{$_CLASS} {
    cursor: pointer;
    display: flex;
    flex-direction: column;
    color: inherit;

    > input[type="radio"] {
        @include utils.srOnly();
        top: 15px; // ensure error tooltip are displayed at the right place
    }

    &-label {
        display: flex;
        @include radioBeforeDefault;
        text-align: left;

        ::slotted(joy-icon) {
            color: var(--joy-color-neutral-40)
        }

        &:before {
            transition: all var(--joy-transition-duration-short) var(--joy-transition-timing-function);
        }

        &::after {
            $_size: 24px;
            content: "";
            position: absolute;
            top: -2px;
            left: -2px;
            width: $_size;
            height: $_size;
            border-radius: 50%;
            transition: box-shadow 150ms ease;
        }
    }

    &-content__wrapper {
        display: flex;
        flex-direction: column;
        padding-left: 27px;

        > #{$_CLASS}-content {
            &:not(:empty) {
                margin-top: var(--joy-core-spacing-4);
            }
        }
    }
    
    &:hover {
        span:before {
            border-color: var(--radio-circle-color-hover);
        }
    }

    &:focus {
        span:before {
            border-color: var(--radio-circle-color-focus);
        }
    }

    &.joy-radio-checked {
        #{$_CLASS}__label {
            font-weight: var(--joy-font-weight-bold);
        }

        ::slotted(joy-icon) {
            color: var(--joy-color-secondary-50);
        }

        .joy-radio-label:before {
            background-color: var(--radio-circle-color);
            box-shadow: inset 0 0 0 2px white;
        }

        &:not(.joy-radio-disabled):hover,
        &:not(.joy-radio-disabled):active {
            .joy-radio-label:before {
                background-color: var(--radio-circle-color-hover);
            }
        }
    }

    &.joy-radio-disabled {
        color: var(--radio-color-disabled);
        cursor: not-allowed;

        .joy-radio-label:before {
            background-color: var(--radio-circle-color-disabled);
            border-color: var(--radio-circle-color-disabled);
        }
    }

    &.joy-radio-invalid {
        .joy-radio-label:before {
            background-color: var(--radio-circle-color-invalid);
            border-color: var(--radio-circle-color-invalid);
        }

        &:hover {
            .joy-radio-label:before {
                border-color: var(--radio-circle-color-invalid-hover);
            }
        }


        &.joy-radio-checked {
            &:hover {
                .joy-radio-label:before {
                    border-color: var(--radio-circle-color-invalid-hover);
                    background-color: var(--radio-circle-color-invalid-hover);
                }
            }
        }
    }

    &#{$_CLASS}--outline {
        border: var(--joy-form-border-width) solid var(--joy-color-neutral-30);
        border-radius: var(--joy-core-radius-3);
        padding: var(--joy-core-spacing-6);
        transition: all var(--joy-transition-duration-short) var(--joy-transition-timing-function);

        .joy-radio-label {
            display: flex;
            align-items: flex-start;

            &:before {
                margin-right: var(--joy-core-spacing-2);
            }
            
        }

        &.joy-radio-checked {
            .joy-radio-label {
                font-weight: bold;
            }
        }

        &.joy-radio-checked,
        &:hover:not(#{$_CLASS}-disabled) {
            border-color: var(--joy-color-secondary-50);
            box-shadow: var(--joy-core-elevation-1);;
        }
    }

    // We trigger opacity animation once the expandable content is fully expanded
    @keyframes opacityAnimation {
        0% {opacity: 0;}
        50% {opacity: 0;}
        100% {opacity: 1;}
    }

    #{$_CLASS}-expandable {
        transition: all var(--joy-transition-duration-default) var(--joy-transition-timing-function);
        height: 0;
        opacity: 0;
        overflow: hidden;

        &--expanded {
            animation: opacityAnimation var(--joy-transition-duration-long) forwards;
            height: auto;
            overflow: visible;
            margin-top: var(--joy-core-spacing-4);
        }

        ::slotted(div[slot="expandable-content"]) {
            display: inline-block;
        }
    }
}


